import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrandIcon, Pill } from '../../ui/common';
import { ViewMode } from '../../domain/types';
import {
  primaryRespondAction,
  toRadarAiAttachment,
  type RadarAiAttachment,
  type WorkThread,
} from '../../domain/radarThreads';
import { workspaceClient } from '../../services/clients';
import type { ChatMessage } from '../../domain/chatTypes';
import { getRadarChatHistory, saveRadarChatHistory } from '../../services/chatHistoryStore';

interface RadarAiAssistantPanelProps {
  activeThread?: WorkThread;
  attachments: RadarAiAttachment[];
  attachableThreads: WorkThread[];
  onNavigate: (view: ViewMode, id: string) => void;
  onAddAttachment: (thread: WorkThread) => void;
  onRemoveAttachment: (id: string) => void;
  openPickerRequest?: number;
  onRestore?: () => void;
}

export const RadarAiAssistantPanel: React.FC<RadarAiAssistantPanelProps> = ({
  activeThread,
  attachments,
  attachableThreads,
  onNavigate,
  onAddAttachment,
  onRemoveAttachment,
  openPickerRequest = 0,
  onRestore,
}) => {
  const [query, setQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(() => getRadarChatHistory());
  const [isSending, setIsSending] = useState(false);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (openPickerRequest > 0) {
      onRestore?.();
      setPickerOpen(true);
    }
  }, [onRestore, openPickerRequest]);

  const fallbackAttachment = useMemo(
    () => (activeThread ? toRadarAiAttachment(activeThread) : undefined),
    [activeThread],
  );

  const suggestions = [
    '¿Qué requiere mi atención ahora?',
    attachments[0]
      ? `Resumen de ${attachments[0].key}`
      : activeThread
        ? `Resumen de ${activeThread.key}`
        : 'Resumen de DEV-3056',
    '¿Qué bloquea al equipo?',
    'Redacta respuesta para Ana en Slack',
  ];

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages.length, isSending]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  const sendQuery = async (raw: string) => {
    const text = raw.trim();
    if (!text || isSending) return;
    const requestId = ++requestIdRef.current;
    setIsSending(true);
    setQuery('');
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      text,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => {
      const next = [...prev, userMsg];
      saveRadarChatHistory(next);
      return next;
    });

    const pack =
      attachments.length > 0 ? attachments : fallbackAttachment ? [fallbackAttachment] : [];

    try {
      const res = await workspaceClient.askRadarAssistant({
        question: text,
        contexts: pack.map((att) => ({
          key: att.key,
          title: att.title,
          summary: att.summary,
          nextStep: att.nextStep,
          sources: att.sources,
        })),
      });
      if (requestId !== requestIdRef.current) return;
      const answer = res.data.answer?.trim() || 'No hay respuesta disponible.';
      const answerMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        text: answer,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => {
        const next = [...prev, answerMsg];
        saveRadarChatHistory(next);
        return next;
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'No se pudo obtener respuesta del asistente.';
      const errorMsg: ChatMessage = {
        id: `a-err-${Date.now()}`,
        role: 'assistant',
        text: message,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => {
        const next = [...prev, errorMsg];
        saveRadarChatHistory(next);
        return next;
      });
    } finally {
      if (requestId === requestIdRef.current) setIsSending(false);
    }
  };

  const contextKey = attachments[0]?.key || activeThread?.key || 'DEV-3056';
  const contextSummary =
    attachments[0]?.summary ||
    activeThread?.aiDiagnostic ||
    'Adjunta un contexto para enfocar las respuestas del asistente.';
  const contextNext =
    attachments[0]?.nextStep ||
    activeThread?.nextStep ||
    'Pasa un contexto desde una card o con el clip de abajo.';
  const focusedThread =
    (attachments[0]
      ? attachableThreads.find((thread) => thread.id === attachments[0]!.id)
      : undefined) || activeThread;
  const primaryAction = focusedThread ? primaryRespondAction(focusedThread) : undefined;
  const pickerCandidates = attachableThreads.filter(
    (thread) => !attachments.some((att) => att.id === thread.id),
  );
  const filteredPickerCandidates = pickerCandidates.filter((thread) => {
    const query = pickerQuery.trim().toLowerCase();
    return (
      !query ||
      thread.key.toLowerCase().includes(query) ||
      thread.title.toLowerCase().includes(query)
    );
  });

  return (
    <div data-testid="radar-ai-assistant-panel" className="z-radar-ai-panel">
      <div
        data-testid="radar-ai-attachments"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--z-dim)' }}>
            Contextos adjuntos
          </span>
          <button
            type="button"
            data-testid="radar-ai-attach-toggle"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
            title="Adjuntar contexto"
            className="z-radar-ai-attach-btn"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            Adjuntar
          </button>
        </div>
        {attachments.length === 0 ? (
          <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
            Ninguno. Usa el clip o “Agregar al contexto” en una tarjeta.
          </span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {attachments.map((att) => (
              <span
                key={att.id}
                data-testid={`radar-ai-attachment-${att.id}`}
                className="z-radar-ai-attachment-chip"
              >
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                  {att.key}
                </span>
                <button
                  type="button"
                  aria-label={`Quitar ${att.key}`}
                  data-testid={`radar-ai-attachment-remove-${att.id}`}
                  onClick={() => onRemoveAttachment(att.id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        {pickerOpen && (
          <div data-testid="radar-ai-attach-picker" className="z-radar-ai-attach-picker">
            <input
              data-testid="radar-ai-attach-search"
              value={pickerQuery}
              onChange={(event) => setPickerQuery(event.target.value)}
              placeholder="Buscar contexto…"
              aria-label="Buscar contexto"
            />
            {filteredPickerCandidates.length === 0 ? (
              <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
                No hay más contextos para adjuntar.
              </span>
            ) : (
              filteredPickerCandidates.slice(0, 8).map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  data-testid={`radar-ai-attach-option-${thread.id}`}
                  onClick={() => {
                    onAddAttachment(thread);
                    setPickerOpen(false);
                    setPickerQuery('');
                  }}
                >
                  <strong>{thread.key}</strong>
                  <span>{thread.title}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div ref={messagesRef} data-testid="radar-ai-messages" className="z-radar-ai-messages">
        {messages.length === 0 && !isSending && (
          <div className="z-radar-ai-empty">
            Escribe una pregunta o elige una sugerencia. Si no hay adjuntos, uso el contexto activo.
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            data-testid={`radar-ai-message-${msg.role}`}
            className={`z-radar-ai-message z-radar-ai-message--${msg.role}`}
          >
            {msg.text}
          </div>
        ))}
        {isSending && (
          <div
            data-testid="radar-ai-thinking"
            className="z-radar-ai-message z-radar-ai-message--assistant"
          >
            Pensando…
          </div>
        )}
      </div>

      <div className="z-radar-ai-composer">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendQuery(query);
            }
          }}
          placeholder="Pregúntame por bloqueos, contexto, prioridades o próximos pasos..."
          rows={2}
          disabled={isSending}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="radar-ai-send"
            onClick={() => void sendQuery(query)}
            disabled={isSending || !query.trim()}
            title="Enviar mensaje"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--z-dim)' }}>Sugerencias</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {suggestions.map((sug) => (
            <button
              key={sug}
              type="button"
              disabled={isSending}
              onClick={() => void sendQuery(sug)}
              className="z-radar-ai-suggestion"
            >
              {sug}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--z-dim)' }}>
          {attachments.length > 0 ? 'Con contextos adjuntos' : 'Con contexto actual'}
        </span>
        <div className="z-radar-ai-context-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <BrandIcon source={focusedThread?.sources[0]?.source || 'slack'} size={14} />
            <span
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 800 }}
            >
              {contextKey}
            </span>
            {focusedThread && (
              <Pill tone={focusedThread.statusTone}>{focusedThread.statusLabel}</Pill>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--z-text-2)', lineHeight: 1.45 }}>
            {contextSummary}
          </p>
          <div className="z-radar-ai-next-box">
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--z-accent-2)' }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
              <span style={{ fontSize: 11.5, fontWeight: 700 }}>Siguiente mejor acción</span>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--z-dim)', lineHeight: 1.35 }}>
              {contextNext}
            </span>
            <button
              type="button"
              onClick={() => {
                if (primaryAction) onNavigate(primaryAction.targetView, primaryAction.targetId);
                else onNavigate('slack', 'SL-eng-carrier');
              }}
              className="z-radar-ai-nav-btn"
            >
              <BrandIcon source={primaryAction?.source || 'slack'} size={13} />
              <span>
                {primaryAction?.source === 'jira'
                  ? 'Ir a Jira'
                  : primaryAction?.source === 'github'
                    ? 'Ir a GitHub'
                    : 'Ir a Slack'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="z-radar-ai-footer">
        <span>Las respuestas pueden tener errores. Verifica siempre.</span>
      </div>
    </div>
  );
};
